import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import { useTranslation } from "react-i18next";

import "../styles/Dashboard.css";
import createTeamConfig from "../../config/createTeamConfig.json";

const Dashboard = ({
  generalCyclists,
  generalRankings,
  espoirRankings,
  races,
  today,
  showSession,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userId } = useAuth();

  const todayDate = useMemo(() => {
    const d = today ? new Date(today) : new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, [today]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const closeDate = useMemo(() => new Date(createTeamConfig.closeDate), []);

  const giroOpenUntil = useMemo(() => {
    const d = new Date(2026, 4, 7);
    d.setHours(23, 59, 59, 999);
    return d;
  }, []);

  const isGiroCreationOpen = useMemo(() => {
    return todayDate <= giroOpenUntil;
  }, [todayDate, giroOpenUntil]);

  const changeableDates = useMemo(() => {
    return Object.entries(createTeamConfig)
      .filter(([key]) => key.startsWith("changeableDate"))
      .map(([, value]) => new Date(value))
      .sort((a, b) => a - b);
  }, []);

  const normalizedToday = useMemo(() => {
    return new Date(Date.UTC(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate()));
  }, [todayDate]);

  const nextChangeableDate = useMemo(() => {
    return changeableDates.find((date) => {
      const normalizedDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      return normalizedDate >= normalizedToday;
    });
  }, [changeableDates, normalizedToday]);

  const transferStatus = useMemo(() => {
    const options = { year: "numeric", month: "2-digit", day: "2-digit" };
    const todayStr = todayDate.toLocaleDateString("fr-CA", options);
    const closeDateStr = closeDate.toLocaleDateString("fr-CA", options);

    if (todayStr >= closeDateStr) {
      if ((generalCyclists || []).length > 0) {
        if (nextChangeableDate) {
          const isToday =
            nextChangeableDate.toISOString().split("T")[0] === normalizedToday.toISOString().split("T")[0];

          if (isToday) return t("dashboard.transfer.accessTransfer");
          return t("dashboard.transfer.nextTransferDate", { date: formatDate(nextChangeableDate) });
        }
        return t("dashboard.transfer.noNextTransfer");
      }
      return t("dashboard.transfer.makeTeam");
    }

    return t("dashboard.transfer.makeTeam");
  }, [todayDate, closeDate, generalCyclists, nextChangeableDate, normalizedToday, t]);

  const displayedTransferStatus = useMemo(() => {
    if (isGiroCreationOpen) {
      return t("dashboard.transfer.makeTeam");
    }

    return transferStatus;
  }, [isGiroCreationOpen, transferStatus, t]);

  const lastRaces = useMemo(() => {
    return (races || [])
      .filter((race) => new Date(race.date_end) < todayDate)
      .sort((a, b) => new Date(b.date_end) - new Date(a.date_end))
      .slice(0, 5);
  }, [races, todayDate]);

  const nextRaces = useMemo(() => {
    return (races || [])
      .filter((race) => new Date(race.date_start) >= todayDate)
      .sort((a, b) => new Date(a.date_start) - new Date(b.date_start))
      .slice(0, 5);
  }, [races, todayDate]);

  const handleRowClick = (raceId) => {
    navigate(`/show-race-detail/${raceId}`);
  };

  const onTransferClick = () => {
    console.log("Transfer status:", displayedTransferStatus);

    if (isGiroCreationOpen) {
      navigate("/manageTeamLeague");
      return;
    }

    if (displayedTransferStatus === t("dashboard.transfer.makeTeam")) {
      navigate("/manageTeamLeague");
    } else if (displayedTransferStatus === t("dashboard.transfer.accessTransfer")) {
      navigate("/changeTeam");
    }
  };

  return (
    <div className="dashboard">
      <div className="dashboard-top">
        <div className="dashboard-top-left">
          <div className="dashboard-header">
            <div className="dashboard-header-title">
              <h2>{t("dashboard.sections.myTeam")}</h2>
              <span onClick={() => showSession("MyTeam")} aria-label={t("dashboard.actions.openMyTeam")}>
                +
              </span>
            </div>

            {todayDate.getDate() === 1 && (
              <strong onClick={() => navigate("/changeBonusCyclist")}>
                {t("dashboard.actions.changeBonusCyclist")}
              </strong>
            )}

            <a
              href="https://www.velofute.com/post/evenepoel-pogacar-van-aert-le-programme-et-calendrier-des-leaders-en-2026"
              target="_blank"
              rel="noreferrer"
            >
              {t("dashboard.links.leadersProgram2026")}
            </a>

            <p onClick={onTransferClick}>
              <strong>{displayedTransferStatus}</strong>
            </p>
          </div>

          <div className="dashboard-top-left-content">
            <div className="dashboard-cyclist-grid">
              {(generalCyclists || []).slice(0, 8).map((cyclist, index) => (
                <div key={index} className="dashboard-cyclist-cell">
                  <div className="dashboard-cyclist-info">
                    <div className="dashboard-cyclist-name">
                      <img
                        src={`${process.env.PUBLIC_URL}/flags/${cyclist.nationality
                          .normalize("NFD")
                          .replace(/[\u0300-\u036f]/g, "")
                          .replace(/ /g, "_")
                          .toLowerCase()}.png`}
                        alt={t("dashboard.aria.flagOf", { country: cyclist.nationality })}
                        width="20"
                        height="20"
                        className="dashboard-cyclist-flag"
                      />
                      <h4>{cyclist.name}</h4>
                    </div>
                    <p>{cyclist.team}</p>
                    <p>
                      {t("dashboard.labels.value")} : <span>{cyclist.finalValue}</span>
                    </p>
                  </div>
                  <div
                    className="dashboard-cyclist-color-bar"
                    style={{ backgroundColor: cyclist.teamColor }}
                  />
                </div>
              ))}
            </div>

            <div className="dashboard-least-profitable">
              <table className="dashboard-least-profitable-table">
                <thead>
                  <tr>
                    <th>{t("dashboard.sections.leastProfitable")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(generalCyclists || [])
                    .slice()
                    .sort((a, b) => (a.cyclistPoints || 0) - (b.cyclistPoints || 0))
                    .slice(0, 5)
                    .map((cyclist, index) => (
                      <tr key={index}>
                        <td>
                          <div className="dashboard-cyclist-row">
                            <img
                              src={`${process.env.PUBLIC_URL}/flags/${cyclist.nationality
                                .normalize("NFD")
                                .replace(/[\u0300-\u036f]/g, "")
                                .replace(/ /g, "_")
                                .toLowerCase()}.png`}
                              alt={t("dashboard.aria.flagOf", { country: cyclist.nationality })}
                              width="20"
                              height="20"
                            />
                            <span>{cyclist.name}</span>
                          </div>
                        </td>
                        <td>{cyclist.cyclistPoints}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="dashboard-top-right">
          <div className="dashboard-header">
            <h2>{t("dashboard.sections.generalRanking")}</h2>
          </div>
          <div className="dashboard-top-right-content">
            <table className="dashboard-ranking-table">
              <thead>
                <tr>
                  <th>{t("dashboard.table.headers.position")}</th>
                  <th>{t("dashboard.table.headers.playerName")}</th>
                  <th>{t("dashboard.table.headers.points")}</th>
                </tr>
              </thead>
              <tbody>
                {(generalRankings || []).slice(0, 6).map((ranking, index) => (
                  <tr
                    key={index}
                    className={userId != null && ranking.userId === userId ? "dashboard-player-row" : ""}
                  >
                    <td>{ranking.position}</td>
                    <td>{ranking.name}</td>
                    <td>{ranking.total_points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="dashboard-bottom">
        <div className="dashboard-bottom-container">
          <div className="dashboard-header">
            <h2>{t("dashboard.sections.myLastRaces")}</h2>
          </div>
          <div className="dashboard-last-races-content">
            <ul className="dashboard-last-races-list">
              {lastRaces.slice(0, 5).map((race, index) => (
                <li
                  key={index}
                  className="dashboard-last-race-item"
                  onClick={() => handleRowClick(race.raceId)}
                >
                  <div className="dashboard-last-race-flag">
                    <img
                      src={`${process.env.PUBLIC_URL}/flags/${race.country
                        .normalize("NFD")
                        .replace(/[\u0300-\u036f]/g, "")
                        .replace(/ /g, "_")
                        .toLowerCase()}.png`}
                      alt={t("dashboard.aria.flagOf", { country: race.country })}
                      width="20"
                      height="20"
                    />
                  </div>
                  <span className="dashboard-last-race-name">{race.name}</span>
                  <span className="dashboard-last-race-dates">{race.racePoints}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="dashboard-bottom-container">
          <div className="dashboard-header">
            <h2>{t("dashboard.sections.myNextRaces")}</h2>
          </div>
          <div className="dashboard-next-races-content">
            <table className="dashboard-next-races-table">
              <thead>
                <tr>
                  <th>{t("dashboard.table.headers.start")}</th>
                  <th>{t("dashboard.table.headers.raceName")}</th>
                  <th>{t("dashboard.table.headers.finish")}</th>
                </tr>
              </thead>
              <tbody>
                {nextRaces.map((race, index) => (
                  <tr key={index}>
                    <td>{formatDate(race.date_start)}</td>
                    <td className="dashboard-race-name">
                      <img
                        src={`${process.env.PUBLIC_URL}/flags/${race.country
                          .normalize("NFD")
                          .replace(/[\u0300-\u036f]/g, "")
                          .replace(/ /g, "_")
                          .toLowerCase()}.png`}
                        alt={t("dashboard.aria.flagOf", { country: race.country })}
                        width="20"
                        height="20"
                      />
                      {race.name}
                    </td>
                    <td>{formatDate(race.date_end)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="dashboard-bottom-container">
          <div className="dashboard-header">
            <h2>{t("dashboard.sections.espoirRanking")}</h2>
          </div>
          <div className="dashboard-top-right-content">
            <table className="dashboard-ranking-table">
              <thead>
                <tr>
                  <th>{t("dashboard.table.headers.position")}</th>
                  <th>{t("dashboard.table.headers.playerName")}</th>
                  <th>{t("dashboard.table.headers.points")}</th>
                </tr>
              </thead>
              <tbody>
                {(espoirRankings || []).slice(0, 7).map((ranking, index) => (
                  <tr
                    key={index}
                    className={userId != null && ranking.userId === userId ? "dashboard-player-row" : ""}
                  >
                    <td>{ranking.position}</td>
                    <td>{ranking.name}</td>
                    <td>{ranking.total_points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
